/**
 * Messaging API
 * Client for the messaging endpoints: conversations, messages, templates.
 */

import { apiClient } from './client'
import type { ApiResponse } from './types'

// ---------- Types ----------

interface ConversationPartnership {
  id: string
  hotel?: { id: string; name: string }
  affiliate?: { id: string; companyName: string; userId: string }
}

interface ConversationLastMessage {
  id: string
  body: string
  bodyFormat?: 'plain' | 'html'
  senderType: string
  senderId: string
  createdAt: string
  deletedAt?: string | null
  hasAttachments: boolean
}

export interface MessageSenderUser {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
}

export interface Conversation {
  id: string
  partnershipId: string
  subject: string | null
  lastMessageAt: string | null
  createdAt: string
  partnership: ConversationPartnership
  lastMessage: ConversationLastMessage | null
  unreadCount: number
  /**
   * S4 (messaging revamp): true when the current viewer has muted
   * this conversation. The list shows a speaker-off icon and the
   * row's unread count is excluded from the sidebar badge.
   */
  muted?: boolean
  /**
   * S6 (messaging revamp): true when the current viewer has archived
   * this conversation. The default Active view hides archived rows;
   * the Archived tab shows ONLY archived rows. Per-user — the other
   * participant still sees the conversation under Active.
   */
  archived?: boolean
}

// S6 (messaging revamp): admin moderation queue types.
export type MessageFlagStatus = 'pending' | 'reviewed' | 'dismissed'

export interface ModerationFlag {
  id: string
  messageId: string
  conversationId: string
  reason: string
  notes: string | null
  status: MessageFlagStatus
  createdAt: string
  reviewedAt: string | null
  reviewedByUserId: string | null
  notesAdmin: string | null
  message: {
    id: string
    senderId: string
    senderType: string
    bodySnippet: string
    bodyFormat: 'plain' | 'html'
    createdAt: string
    deletedAt: string | null
    hiddenAt: string | null
    hiddenByUserId: string | null
  } | null
  reporter: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string | null
  } | null
}

/**
 * S4: parent-message snippet used by the "Replying to ..." chip
 * above a message bubble. The body is truncated server-side (160
 * chars). When `deletedAt` is non-null the FE renders the
 * "[message deleted]" placeholder instead of the body.
 */
export interface ReplyTargetSnippet {
  id: string
  body: string
  bodyFormat: 'plain' | 'html'
  senderUserId: string
  senderName: string | null
  deletedAt: string | null
}

/**
 * S4: pinned-message tray row. Shape mirrors the BE response from
 * GET /messaging/conversations/:id/pinned.
 */
export interface PinnedMessage {
  id: string
  conversationId: string
  senderId: string
  senderType: string
  body: string
  bodyFormat: 'plain' | 'html'
  createdAt: string
  pinnedAt: string
  pinnedByUserId: string | null
  deletedAt: string | null
}

export interface MessageAttachment {
  id: string
  messageId: string | null
  fileName: string
  /** Short-lived signed URL — re-issued on each read. */
  fileUrl: string
  mimeType: string | null
  fileSize: number | null
  imageWidth?: number | null
  imageHeight?: number | null
  storageObjectKey?: string | null
  createdAt: string
}

/** S3: result of POST /messaging/attachments/upload-url. */
interface IssuedUpload {
  attachmentId: string
  uploadUrl: string
  uploadHeaders: Record<string, string>
  storageObjectKey: string
  expiresAt: string
  signed: boolean
}

export const ATTACHMENT_LIMITS = {
  maxBytesPerFile: 10 * 1024 * 1024,
  maxAttachmentsPerMessage: 5,
} as const

export const ALLOWED_ATTACHMENT_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
]

export interface Message {
  id: string
  conversationId: string
  senderId: string
  senderType: string
  /**
   * The actual platform user who wrote this message — populated by
   * the backend from user_service.users joined via senderId. Used
   * for two-line attribution ("Hotel Name / Idan Brody"). Null if
   * the user record can't be resolved.
   */
  senderUser?: MessageSenderUser | null
  /** 'plain' (default) or 'html' from the Tiptap composer. */
  bodyFormat?: 'plain' | 'html'
  body: string
  readAt: string | null
  /**
   * Set when the sender (or an admin) soft-deletes the message.
   * Body is redacted server-side; render a "Message deleted"
   * placeholder when this is set.
   */
  deletedAt?: string | null
  createdAt: string
  attachments: MessageAttachment[]
  // S4 (messaging revamp): pin metadata. pinnedAt is the canonical
  // "is this pinned" signal. pinnedByUserId is for future audit /
  // moderation surfaces — not rendered today.
  pinnedAt?: string | null
  pinnedByUserId?: string | null
  // S4: reply-to. The id is set on outbound (via Composer); the full
  // snippet is hydrated by the BE on read so the bubble can render
  // the parent-chip without an extra fetch.
  replyToMessageId?: string | null
  replyToMessage?: ReplyTargetSnippet | null
  // S6 (messaging revamp): admin moderation hide. DISTINCT from
  // soft-delete (deletedAt). When set, the BE has already redacted
  // body/attachments; the FE renders the "hidden by administrator"
  // placeholder so participants can tell what happened.
  hiddenAt?: string | null
  hiddenByUserId?: string | null
}

export interface MessageTemplate {
  id: string
  hotelId: string | null
  name: string
  subject: string | null
  body: string
  category: string
  isSystem: boolean
  createdAt: string
  // S5 (messaging revamp): scope-aware visibility.
  scope?: 'system' | 'org' | 'user'
  /** For scope='org' — the hotel or affiliate that owns the template. */
  orgId?: string | null
  /** For scope='user' — the user who created (and exclusively owns) the template. */
  createdByUserId?: string | null
}

/** S5 result of POST /messaging/bulk-send. */
export interface BulkSendResult {
  sent: number
  failed: Array<{ conversationId: string; error: string }>
}

// ---------- API ----------

export const messagingApi = {
  /**
   * List conversations for the current user
   */
  getConversations: (): Promise<ApiResponse<Conversation[]>> => {
    return apiClient.get('/messaging/conversations')
  },

  /**
   * Create a new conversation
   */
  createConversation: (partnershipId: string, subject?: string): Promise<ApiResponse<Conversation>> => {
    // subject is optional server-side; keep inferred body shape optional too.
    const body: { partnershipId: string; subject?: string } = { partnershipId }
    if (subject !== undefined) body.subject = subject
    return apiClient.post('/messaging/conversations', body)
  },

  /**
   * Get messages for a conversation (paginated, newest first)
   */
  getMessages: (
    conversationId: string,
    params?: { limit?: number; before?: string }
  ): Promise<ApiResponse<Message[]> & { hasMore?: boolean }> => {
    return apiClient.get(`/messaging/conversations/${conversationId}/messages`, { params })
  },

  /**
   * Send a message. S3 prefers `attachmentKeys` (pre-uploaded via
   * requestUploadUrl + PUT) — JSON body, no multipart. The legacy
   * `files` shape stays for back-compat during the FE migration window.
   */
  sendMessage: (
    conversationId: string,
    body: string,
    files?: File[],
    bodyFormat: 'plain' | 'html' = 'plain',
    attachmentKeys?: string[],
    replyToMessageId?: string,
  ): Promise<ApiResponse<Message>> => {
    if (!files || files.length === 0) {
      return apiClient.post(`/messaging/conversations/${conversationId}/messages`, {
        body,
        bodyFormat,
        attachmentKeys: attachmentKeys && attachmentKeys.length > 0 ? attachmentKeys : undefined,
        // S4: pass through the optional reply-target id. BE validates
        // it's in the same conversation and rejects with 404/422 if not.
        replyToMessageId: replyToMessageId || undefined,
      })
    }
    const formData = new FormData()
    formData.append('body', body)
    formData.append('bodyFormat', bodyFormat)
    if (replyToMessageId) formData.append('replyToMessageId', replyToMessageId)
    files.forEach((file) => formData.append('files', file))
    return apiClient.post(`/messaging/conversations/${conversationId}/messages`, formData)
  },

  /** S3 step 1: request a signed PUT URL. */
  requestUploadUrl: (params: {
    conversationId: string
    fileName: string
    mimeType: string
    sizeBytes: number
    imageWidth?: number
    imageHeight?: number
  }): Promise<ApiResponse<IssuedUpload>> => {
    return apiClient.post('/messaging/attachments/upload-url', params)
  },

  /**
   * S3 step 2: PUT (or POST multipart for fallback) bytes to the URL.
   * Bypasses apiClient because GCS URLs are absolute.
   */
  uploadBytesToSignedUrl: async (issued: IssuedUpload, file: File): Promise<void> => {
    if (
      !issued.signed &&
      issued.uploadUrl.includes('/attachments/') &&
      issued.uploadUrl.includes('/upload')
    ) {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch(issued.uploadUrl, { method: 'POST', body: fd, credentials: 'include' })
      if (!r.ok) throw new Error(`upload failed: ${r.status}`)
      return
    }
    const r = await fetch(issued.uploadUrl, {
      method: 'PUT',
      headers: issued.uploadHeaders,
      body: file,
    })
    if (!r.ok) throw new Error(`upload failed: ${r.status}`)
  },

  /**
   * Mark all messages in a conversation as read
   */
  markAsRead: (conversationId: string): Promise<ApiResponse<{ markedAsRead: number }>> => {
    return apiClient.patch(`/messaging/conversations/${conversationId}/read`)
  },

  /**
   * Soft-delete a message. Server requires the caller to be the
   * sender (or admin). The row stays in the database; both sides
   * will see a "Message deleted" placeholder.
   */
  deleteMessage: (messageId: string): Promise<ApiResponse<{ id: string; deletedAt: string }>> => {
    return apiClient.delete(`/messaging/messages/${messageId}`)
  },

  /**
   * Get total unread count for the current user
   */
  getUnreadCount: (): Promise<ApiResponse<{ unreadCount: number }>> => {
    return apiClient.get('/messaging/unread-count')
  },

  /**
   * List message templates visible to the current user.
   *
   * S5 (messaging revamp): the BE returns the union of system + the
   * actor's own user-scope + accessible org-scope templates. The
   * optional `orgId` arg narrows the org branch to a single
   * hotel/affiliate. `scope` arg post-filters server-side (useful for
   * the "show only my own" filter chip in the picker).
   */
  getTemplates: (params?: { orgId?: string; scope?: 'system' | 'org' | 'user' }): Promise<ApiResponse<MessageTemplate[]>> => {
    const q: Record<string, string> = {}
    if (params?.orgId) q.orgId = params.orgId
    if (params?.scope) q.scope = params.scope
    return apiClient.get('/messaging/templates', { params: q })
  },

  /**
   * Create a message template. S5: scope is required; org-scope must
   * supply orgId; user-scope is tied to the actor server-side.
   */
  createTemplate: (data: {
    scope: 'system' | 'org' | 'user'
    name: string
    body: string
    subject?: string
    category?: string
    orgId?: string
  }): Promise<ApiResponse<MessageTemplate>> => {
    return apiClient.post('/messaging/templates', data)
  },

  /** Update a non-system template the actor owns. */
  updateTemplate: (
    id: string,
    data: { name?: string; body?: string; subject?: string; category?: string },
  ): Promise<ApiResponse<MessageTemplate>> => {
    return apiClient.patch(`/messaging/templates/${id}`, data)
  },

  /** Delete a non-system template the actor owns. */
  deleteTemplate: (id: string): Promise<ApiResponse<void>> => {
    return apiClient.delete(`/messaging/templates/${id}`)
  },

  /**
   * S5: bulk-send to up to 100 recipient conversations. The BE returns
   * a partial-failure envelope ({sent, failed[]}) even on success.
   * The 422 boundary (>100 recipients) is enforced server-side; FE
   * also gates the submit button at 100 to match.
   */
  bulkSend: (data: {
    recipientConversationIds: string[]
    body: string
    bodyFormat?: 'plain' | 'html'
    attachmentKeys?: string[]
  }): Promise<ApiResponse<BulkSendResult>> => {
    return apiClient.post('/messaging/bulk-send', data)
  },

  // ---------- S2 (messaging revamp) ----------

  /** Search messages across the user's scope. Hits migration 110 indexes. */
  searchMessages: (params: {
    q: string
    conversationId?: string
    page?: number
    limit?: number
  }): Promise<ApiResponse<MessageSearchResult>> => {
    return apiClient.get('/messaging/search', { params })
  },

  /** List conversations with S2 filters (unread/entityType/entityId/hasAttachments). */
  getConversationsFiltered: (filters: {
    unread?: boolean
    hasAttachments?: boolean
    entityType?: 'hotel' | 'affiliate'
    entityId?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Conversation[]>> => {
    const params: Record<string, string | number> = {}
    if (filters.unread) params.unread = 'true'
    if (filters.hasAttachments) params.hasAttachments = 'true'
    if (filters.entityType) params.entityType = filters.entityType
    if (filters.entityId) params.entityId = filters.entityId
    if (filters.limit !== undefined) params.limit = filters.limit
    if (filters.offset !== undefined) params.offset = filters.offset
    return apiClient.get('/messaging/conversations', { params })
  },

  /**
   * Bulk mark-as-read. scope='all' clears every in-scope conversation;
   * scope={conversationId} is equivalent to markAsRead. The BE fires a
   * `conversation:all-read` event on the actor's user-room after the
   * write lands so other devices update their badges without polling.
   */
  markAllAsRead: (
    scope: 'all' | { conversationId: string } = 'all',
  ): Promise<ApiResponse<{ affectedCount: number; conversationIds: string[] }>> => {
    return apiClient.post('/messaging/mark-all-read', { scope })
  },

  // ---------- S4 (messaging revamp) ----------

  /** Mute a conversation for the current user. Idempotent. */
  muteConversation: (
    conversationId: string,
  ): Promise<ApiResponse<{ mutedAt: string }>> => {
    return apiClient.post(`/messaging/conversations/${conversationId}/mute`)
  },

  /** Unmute a conversation for the current user. Idempotent. */
  unmuteConversation: (
    conversationId: string,
  ): Promise<ApiResponse<{ wasMuted: boolean }>> => {
    return apiClient.delete(`/messaging/conversations/${conversationId}/mute`)
  },

  /** Pin a message. Auth: participant or admin. */
  pinMessage: (
    messageId: string,
  ): Promise<ApiResponse<{ id: string; pinnedAt: string; pinnedByUserId: string }>> => {
    return apiClient.post(`/messaging/messages/${messageId}/pin`)
  },

  /** Unpin a message. Idempotent. */
  unpinMessage: (
    messageId: string,
  ): Promise<ApiResponse<{ id: string; pinnedAt: null }>> => {
    return apiClient.delete(`/messaging/messages/${messageId}/pin`)
  },

  /** List pinned messages in a conversation (newest-pinned first, cap=10). */
  getPinnedMessages: (
    conversationId: string,
  ): Promise<ApiResponse<PinnedMessage[]>> => {
    return apiClient.get(`/messaging/conversations/${conversationId}/pinned`)
  },

  // ---------- S6 (messaging revamp) ----------

  /** Archive a conversation for the current viewer. Idempotent. */
  archiveConversation: (
    conversationId: string,
  ): Promise<ApiResponse<{ archivedAt: string }>> => {
    return apiClient.post(`/messaging/conversations/${conversationId}/archive`)
  },

  /** Un-archive a conversation. Idempotent. */
  unarchiveConversation: (
    conversationId: string,
  ): Promise<ApiResponse<{ wasArchived: boolean }>> => {
    return apiClient.delete(`/messaging/conversations/${conversationId}/archive`)
  },

  /**
   * S6: list conversations with the Active/Archived view toggle.
   * archived=true → ONLY archived; archived=false or undefined →
   * default Active view (the existing getConversations preserves
   * this exact semantic too).
   */
  getConversationsArchived: (
    archived: boolean,
  ): Promise<ApiResponse<Conversation[]>> => {
    return apiClient.get('/messaging/conversations', {
      params: { archived: archived ? 'true' : 'false' },
    })
  },

  /** Flag a message (any participant). Idempotent on duplicate pending. */
  flagMessage: (
    messageId: string,
    body: { reason: string; notes?: string },
  ): Promise<ApiResponse<{ id: string; status: MessageFlagStatus; createdAt?: string; duplicate?: boolean }>> => {
    return apiClient.post(`/messaging/messages/${messageId}/flag`, body)
  },

  /** Admin moderation queue (paginated). */
  listAdminFlags: (params?: {
    status?: MessageFlagStatus
    limit?: number
    offset?: number
  }): Promise<ApiResponse<ModerationFlag[]> & { total?: number }> => {
    const q: Record<string, string | number> = {}
    if (params?.status) q.status = params.status
    if (params?.limit !== undefined) q.limit = params.limit
    if (params?.offset !== undefined) q.offset = params.offset
    return apiClient.get('/messaging/admin/flags', { params: q })
  },

  /** Admin: hide or dismiss a flag. */
  reviewAdminFlag: (
    flagId: string,
    body: { decision: 'hide' | 'dismiss'; notes?: string },
  ): Promise<ApiResponse<{ ok: true }>> => {
    return apiClient.post(`/messaging/admin/flags/${flagId}/review`, body)
  },

  /** Admin: undo a previous hide on a message. */
  unhideAdminMessage: (
    messageId: string,
  ): Promise<ApiResponse<{ ok: true }>> => {
    return apiClient.post(`/messaging/admin/messages/${messageId}/unhide`)
  },
}

// S2 search result types.
export interface MessageSearchHit {
  messageId: string
  conversationId: string
  snippet: string
  createdAt: string
  senderUserId: string
  matchedSpan: { start: number; end: number } | null
}

export interface MessageSearchResult {
  items: MessageSearchHit[]
  total: number
}

export default messagingApi
